// Offerings module parser — maps the per-property offering endpoints' wire
// DTOs to camelCase view-model types. The offering detail DTO is already
// camelCase (aliased in the BE query); the OfferingPropertyDto list is still
// snake_case. (The /api/admin/offerings queue endpoint already returns
// camelCase and flows straight through, no parser needed.)

import type { PaginatedResponse } from '@shared/types/pagination'
import { OFFERING_STATUS } from '../constants/offeringStatus'
import type {
  ApproveOfferingResponseDto,
  OfferingDto,
  OfferingPropertyDto,
  OfferingStatusCountsDto,
  OfferingWhitepaperDto,
  PropertyFinancialsDto,
  VaspListingDto,
  VaspSubmitStepDto,
} from './offeringContracts'
import type {
  ApproveOfferingOutcome,
  Offering,
  OfferingListItem,
  OfferingProperty,
  OfferingPropertyFinancials,
  OfferingWhitepaper,
  VaspListing,
} from '../types/offeringTypes'
import type { VaspSubmitStepResult } from '@shared/constants/vaspSubmit'

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

export const parsePropertyFinancials = (
  dto: PropertyFinancialsDto,
): OfferingPropertyFinancials => ({
  netYieldPercentage: toNumberOrNull(dto.netYieldPercentage),
})

export const parseOfferingProperty = (dto: OfferingPropertyDto): OfferingProperty => ({
  id: String(dto.id ?? ''),
  propertyName: String(dto.property_name ?? '—'),
  propertyType: String(dto.property_type ?? '—'),
  propertyStatus: String(dto.property_status ?? '—'),
  city: String(dto.city ?? '—'),
  image: String(dto.property_image ?? dto.image ?? ''),
})

// The /api/admin/offerings queue + single-offering endpoints return camelCase
// rows that already match OfferingListItem. This identity parser keeps the parse
// seam in one place (data-fetching.md: "every response goes through a parser,
// even an identity one") so a future server rename breaks here, not in every
// component that binds to OfferingListItem.
export const parseOfferingListItem = (dto: OfferingListItem): OfferingListItem => dto

// Shape variants the queue list response can arrive in (bare array, a
// { rows } / { data } envelope, with or without a meta block). Kept here so the
// envelope-normalization + pagination math live in the parser, never the
// endpoint or a container.
interface OfferingsListRawDto {
  data?: OfferingListItem[] | OfferingsListRawDto
  rows?: OfferingListItem[]
  meta?: { total?: number; page?: number; limit?: number }
  total?: number
  page?: number
  limit?: number
}

const DEFAULT_OFFERINGS_PAGE_LIMIT = 20

export const parseAllOfferingsList = (
  raw: OfferingsListRawDto,
): PaginatedResponse<OfferingListItem> => {
  const envelope: OfferingListItem[] | OfferingsListRawDto = raw?.data ?? raw
  const rows: OfferingListItem[] = Array.isArray(envelope)
    ? envelope
    : Array.isArray(envelope.rows)
      ? envelope.rows
      : Array.isArray(envelope.data)
        ? envelope.data
        : []

  const nonArrayEnvelope = Array.isArray(envelope) ? undefined : envelope
  const totalItems = raw?.meta?.total ?? nonArrayEnvelope?.total ?? rows.length
  const page = raw?.meta?.page ?? nonArrayEnvelope?.page ?? 1
  const limit = raw?.meta?.limit ?? nonArrayEnvelope?.limit ?? DEFAULT_OFFERINGS_PAGE_LIMIT

  return {
    data: rows.map(parseOfferingListItem),
    pagination: {
      page,
      limit,
      totalItems,
      totalPages: Math.max(1, Math.ceil(totalItems / Math.max(limit, 1))),
    },
  }
}

export const parseOfferingStatusCounts = (dto: OfferingStatusCountsDto): OfferingStatusCountsDto =>
  dto

export const parseOffering = (dto: OfferingDto): Offering => ({
  id: String(dto.id ?? ''),
  offeringTitle: String(dto.offeringTitle ?? ''),
  offeringType: String(dto.offeringType ?? 'primary'),
  offeringStatus: String(dto.offeringStatus ?? OFFERING_STATUS.DRAFT),
  listingPrice: dto.listingPrice ?? 0,
  totalTokens: Number(dto.totalTokens ?? 0),
  minInvestment: dto.minInvestmentAmount ?? 0,
  maxInvestment: dto.maxInvestmentAmount ?? 0,
  investmentWindowStart: dto.investmentWindowStart ?? null,
  investmentWindowEnd: dto.investmentWindowEnd ?? null,
  expRentalYield: dto.expectedRentalYield ?? 0,
  distributionFrequency: String(dto.rentalDistributionFrequency ?? 'monthly'),
  expAppreciation: dto.expectedCapitalAppreciation ?? 0,
  targetRoi5yr: dto.targetRoi5yr ?? 0,
  description: String(dto.offeringDescription ?? ''),
})

// Normalises the nullable tail of the VASP listing DTO so the drawer binds to a
// fully-populated shape and never has to distinguish "absent key" from "null".
// Two renames earn their keep here. The wire's `error` / `errorSource` become
// `lastError` / `lastErrorSource` so the outcome satisfies VaspFailureInput by
// field name, the way every other VAISP view model does — the approve dialog then
// shares the drawer's banner grammar with no adapter. The steps stay lossless in
// the shared wire shape for the same reason parseVaspSubmitResponse does it:
// `submitStepViews` owns the labels and fallbacks.
const parseVaspSubmitStep = (dto: VaspSubmitStepDto): VaspSubmitStepResult => ({
  step: dto.step ?? null,
  result: dto.result
    ? { status: dto.result.status ?? null, failureReason: dto.result.failureReason ?? null }
    : null,
})

export const parseApproveOfferingOutcome = (
  dto: ApproveOfferingResponseDto | null | undefined,
): ApproveOfferingOutcome => {
  const vasp = dto?.vaspListing
  // No outcome in the body: an admin-service that predates this contract answered
  // 204. The approval still committed and the hand-off still ran best-effort, so
  // resolve it the way that flow always read — nothing to report — rather than
  // inventing a failure the operator cannot act on.
  if (!vasp) {
    return {
      offeringStatus: String(dto?.offeringStatus ?? ''),
      vaspListing: {
        listingId: null,
        listingStatus: null,
        created: true,
        submitted: true,
        steps: [],
        lastError: null,
        lastErrorSource: null,
      },
    }
  }

  return {
    offeringStatus: String(dto?.offeringStatus ?? ''),
    vaspListing: {
      listingId: vasp.listingId ?? null,
      listingStatus: vasp.listingStatus ?? null,
      created: Boolean(vasp.created),
      submitted: Boolean(vasp.submitted),
      steps: (vasp.steps ?? []).map(parseVaspSubmitStep),
      lastError: vasp.error ?? null,
      lastErrorSource: vasp.errorSource ?? null,
    },
  }
}

export const parseVaspListing = (dto: VaspListingDto): VaspListing => ({
  offeringId: String(dto.offeringId ?? ''),
  listingId: String(dto.listingId ?? ''),
  instrumentId: String(dto.instrumentId ?? ''),
  status: String(dto.status ?? ''),
  tokenQuantity: dto.tokenQuantity ?? null,
  tokensFilled: dto.tokensFilled ?? null,
  pricePerToken: dto.pricePerToken ?? null,
  value: dto.value ?? null,
  currency: dto.currency ?? null,
  blockchainNetwork: dto.blockchainNetwork ?? null,
  tokenName: dto.tokenName ?? null,
  tokenHex: dto.tokenHex ?? null,
  issuerAddress: dto.issuerAddress ?? null,
  investmentWindowStart: dto.investmentWindowStart ?? null,
  investmentWindowEnd: dto.investmentWindowEnd ?? null,
  listingSubmittedAt: dto.listingSubmittedAt ?? null,
  // DLD band. Monetary fields go through toNumberOrNull because the server
  // sends AED as numbers here but the offering endpoints next door send the same
  // figures as decimal strings — normalising at the seam keeps the band
  // arithmetic and the money formatter from ever seeing a string.
  listingPrice: toNumberOrNull(dto.listingPrice),
  dldValuation: toNumberOrNull(dto.dldValuation),
  dldValuationDate: dto.dldValuationDate ?? null,
  // `?? 0` rather than a literal band width: a missing tolerance means the
  // percentage is unknown, and `dldBandView` then omits the "±N%" wording
  // entirely rather than asserting a number the provider never sent.
  dldValuationTolerancePercent: Number(dto.dldValuationTolerancePercent ?? 0),
  dldValuationMinListingPrice: toNumberOrNull(dto.dldValuationMinListingPrice),
  dldValuationMaxListingPrice: toNumberOrNull(dto.dldValuationMaxListingPrice),
  // NOT `?? false`. Null is "not assessable" and is preserved all the way to
  // the view so it can be rendered as unknown — see ../utils/dldValuationBand.ts.
  isListingPriceWithinDldBand: dto.isListingPriceWithinDldBand ?? null,
  lastWebhookEvent: dto.lastWebhookEvent ?? null,
  lastWebhookAt: dto.lastWebhookAt ?? null,
  lastError: dto.lastError ?? null,
  lastErrorSource: dto.lastErrorSource ?? null,
  lastRequestId: dto.lastRequestId ?? null,
  lastSyncedAt: dto.lastSyncedAt ?? null,
})

/**
 * `isDownloadable` keys off the server's flag alone and defaults to false: a
 * document announced but never fetched has no stored bytes, and offering a
 * download for it would 404. Defaulting true would make the un-fetched state —
 * the common one while a review is outstanding — look ready.
 */
export function parseOfferingWhitepaper(dto: OfferingWhitepaperDto): OfferingWhitepaper {
  return {
    instrumentId: dto?.instrumentId ?? null,
    documents: (dto?.documents ?? []).map((document) => ({
      documentId: document.documentId ?? '',
      documentType: document.documentType ?? '',
      fileName: document.fileName ?? null,
      fileSizeBytes: document.fileSizeBytes ?? null,
      mimeType: document.mimeType ?? null,
      issuedAt: document.issuedAt ?? null,
      isDownloadable: document.isDownloadable === true,
      fileUrl: document.fileUrl ?? null,
      publishedUrl: document.publishedUrl ?? null,
      retryCount: document.retryCount ?? 0,
    })),
    whitepaperDocumentId: dto?.whitepaperDocumentId ?? null,
  }
}
